import { PricingRule, PricingCondition } from '@/types/revenue-manager';

/**
 * API format for pricing rule condition
 */
export interface ApiPricingCondition {
  type: string;
  operator: string;
  value: string | number;
  logicOperator: 'AND' | 'OR' | null;
  eventDetails?: {
    eventId: string;
    eventName: string;
    startDate: string;
    endDate: string;
  };
}

/**
 * API format for pricing rule
 */
export interface ApiPricingRule {
  id?: string;
  name: string;
  org_id: string;
  property_id: number;
  priority: number;
  isActive: boolean;
  ruletype: 'restriction';
  conditions: ApiPricingCondition[];
  action: {
    adjustmentType: 'percentage' | 'fixed';
    adjustmentValue: number;
    applyTo: 'base_price' | 'current_price';
  };
  roomTypes: number[];
  roomVariants: number[];
  otaChannels: string[];
  applicable_cm_names: string[];
  applicableTo: {
    startDate: string;
    endDate: string | null;
    daysOfWeek: string[] | null;
    timeOfDay: string | null;
  };
  executionType: {
    processingFrequency: 'manual' | 'daily' | 'weekly' | 'monthly' | 'specific' | 'immediate' | 'hourly';
    executionDays: number[];
    executionTime: string | null;
    executionStartDate: string | null;
    executionEndDate: string | null;
  };
  submitted_by?: string;
  created_at?: string;
  updated_at?: string;
  last_executed_at?: string;
}

/**
 * Raw API response format (can have either snake_case or camelCase)
 */
export interface RawApiPricingRule {
  id?: string;
  name?: string;
  org_id?: string;
  property_id?: number;
  propertyId?: number;
  priority?: number;
  isActive?: boolean;
  is_active?: boolean;
  ruletype?: string;
  rule_type?: string;
  conditions?: ApiPricingCondition[];
  action?: {
    adjustmentType?: 'percentage' | 'fixed';
    adjustmentValue?: number;
    applyTo?: 'base_price' | 'current_price';
  };
  roomTypes?: number[];
  room_types?: number[];
  roomVariants?: number[];
  room_variants?: number[];
  otaChannels?: string[];
  ota_channels?: string[];
  applicable_cm_names?: string[];
  applicable_cm_name?: string; // API returns singular string
  applicateCmNames?: string[];
  applicableTo?: {
    startDate?: string;
    endDate?: string | null;
    daysOfWeek?: string[] | null;
    timeOfDay?: string | null;
  };
  applicable_to?: {
    startDate?: string;
    endDate?: string | null;
    daysOfWeek?: string[] | null;
    timeOfDay?: string | null;
  };
  executionType?: {
    processingFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly' | 'specific' | 'immediate' | 'hourly';
    executionDays?: number[];
    executionTime?: string | null;
    executionStartDate?: string | null;
    executionEndDate?: string | null;
  };
  execution_type?: {
    processingFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly' | 'specific' | 'immediate' | 'hourly';
    executionDays?: number[];
    executionTime?: string | null;
    executionStartDate?: string | null;
    executionEndDate?: string | null;
  };
  submitted_by?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  last_executed_at?: string;
  lastExecutedAt?: string;
}

/**
 * Day of week mapping for API format
 */
const daysOfWeekMap: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

/**
 * Condition type mapping from UI to API format
 */
const conditionTypeMap: Record<string, string> = {
  daysUntilCheckIn: 'daysBefore',
  occupancy: 'occupancy',
  dayOfWeek: 'dayOfWeek',
  event: 'event',
};

/**
 * Transform pricing rule from dialog format to API format
 */
export function transformPricingRuleForAPI(
  rule: Omit<PricingRule, 'id' | 'createdAt' | 'updatedAt'> & { applicateCmNames?: string[] },
  orgId: string | number,
  propertyId: string | number,
  branchId: string | number,
  api_key: string | number
): ApiPricingRule {
  // Transform conditions
  const transformedConditions = rule.conditions.map((condition, index) => ({
    type: conditionTypeMap[condition.type] || condition.type,
    operator: condition.operator,
    value: condition.value,
    logicOperator: index < rule.conditions.length - 1 ? (condition.logic || 'AND') : null,
    ...(condition.eventDetails && { eventDetails: condition.eventDetails }),
  }));

  // Transform days of week
  const daysOfWeek = rule.daysOfWeek?.map(dayIndex => daysOfWeekMap[dayIndex]) || [];

  // Build the API format
  // If api_key is available, set otaChannels to empty array
  const otaChannelsArray = api_key ? [] : (rule.otaChannels || []);
  const result: any = {
    name: rule.name,
    org_id: String(orgId),
    property_id: branchId, // Use branch_id as property_id
    priority: rule.priority,
    isActive: rule.isActive,
    ruletype: 'restriction',
    conditions: transformedConditions,
    action: {
      adjustmentType: rule.adjustmentType,
      adjustmentValue: rule.adjustmentValue,
      applyTo: rule.applyTo === 'basePrice' ? 'base_price' : 'current_price',
    },
    roomTypes: rule.roomTypes.map(id => id),
    roomVariants: rule.selectedVariants?.map(v => v.variantId) || [],
    otaChannels: otaChannelsArray,
    ota_channels: otaChannelsArray, // Add snake_case for API compatibility
    applicable_cm_names: rule.applicateCmNames || [],
    applicableTo: {
      startDate: rule.startDate,
      endDate: rule.endDate || null,
      daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : null,
      timeOfDay: rule.timeOfDay || null,
    },
    executionType: {
      processingFrequency: rule.processingFrequency,
      executionDays: rule.executionDays || [],
      executionTime: rule.executionTime || null,
      executionStartDate: rule.executionStartDate || null,
      executionEndDate: rule.executionEndDate || null,
    },
  };

  // Only add api_key if it's not empty
  if (api_key) {
    result.api_key = api_key;
  }

  return result;
}

/**
 * Normalize API response to handle both snake_case and camelCase field names
 */
function normalizeApiRule(rawRule: RawApiPricingRule): ApiPricingRule {
  return {
    id: rawRule.id,
    name: rawRule.name || 'Unnamed Rule',
    org_id: rawRule.org_id || '',
    property_id: (rawRule.property_id ?? rawRule.propertyId) || 0,
    priority: rawRule.priority || 0,
    isActive: rawRule.isActive ?? rawRule.is_active ?? true,
    ruletype: (rawRule.ruletype || rawRule.rule_type || 'restriction') as 'restriction',
    conditions: rawRule.conditions || [],
    action: rawRule.action ? {
      adjustmentType: rawRule.action.adjustmentType || 'percentage',
      adjustmentValue: rawRule.action.adjustmentValue ?? 0,
      applyTo: rawRule.action.applyTo || 'base_price'
    } : {
      adjustmentType: 'percentage' as const,
      adjustmentValue: 0,
      applyTo: 'base_price' as const
    },
    roomTypes: (rawRule.roomTypes || rawRule.room_types || []) as number[],
    roomVariants: (rawRule.roomVariants || rawRule.room_variants || []) as number[],
    otaChannels: (rawRule.otaChannels || rawRule.ota_channels || []) as string[],
    applicable_cm_names: (() => {
      // Handle both array and singular string from API
      if (rawRule.applicable_cm_names) return rawRule.applicable_cm_names;
      if (rawRule.applicable_cm_name) return rawRule.applicable_cm_name ? [rawRule.applicable_cm_name] : [];
      if ((rawRule as any).applicateCmNames) return (rawRule as any).applicateCmNames;
      return [];
    })(),
    applicableTo: (rawRule.applicableTo || rawRule.applicable_to || {
      startDate: new Date().toISOString().split('T')[0],
      endDate: null,
      daysOfWeek: null,
      timeOfDay: null,
    }) as ApiPricingRule['applicableTo'],
    executionType: (rawRule.executionType || rawRule.execution_type || {
      processingFrequency: 'manual' as const,
      executionDays: [],
      executionTime: null,
      executionStartDate: null,
      executionEndDate: null,
    }) as ApiPricingRule['executionType'],
    submitted_by: rawRule.submitted_by,
    created_at: rawRule.created_at || rawRule.createdAt,
    updated_at: rawRule.updated_at || rawRule.updatedAt,
    last_executed_at: rawRule.last_executed_at || rawRule.lastExecutedAt,
  };
}

/**
 * Transform pricing rule from API format to dialog format
 */
export function transformPricingRuleFromAPI(rawApiRule: RawApiPricingRule, roomTypesData?: any[]): PricingRule {
  // Normalize the API rule first
  const apiRule = normalizeApiRule(rawApiRule);
  
  // Reverse day mapping
  const reverseDaysMap: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  // Reverse condition type mapping
  const reverseConditionTypeMap: Record<string, string> = {
    daysBefore: 'daysUntilCheckIn',
    occupancy: 'occupancy',
    dayOfWeek: 'dayOfWeek',
    event: 'event',
  };

  // Transform conditions back with safety checks
  const transformedConditions: PricingCondition[] = (apiRule.conditions || []).map((condition: ApiPricingCondition) => {
    const conditionType = (reverseConditionTypeMap[condition.type] || condition.type) as PricingCondition['type'];
    
    // For event conditions, use eventId from eventDetails if available
    let conditionValue = condition.value;
    if (conditionType === 'event' && condition.eventDetails?.eventId) {
      conditionValue = condition.eventDetails.eventId;
    }
    
    return {
      type: conditionType,
      operator: condition.operator as PricingCondition['operator'],
      value: conditionValue,
      logic: condition.logicOperator || undefined,
      ...(condition.eventDetails && { eventDetails: condition.eventDetails }),
    };
  });

  // Transform days of week back
  const daysOfWeek = apiRule.applicableTo?.daysOfWeek
    ? apiRule.applicableTo.daysOfWeek.map((day: string) => reverseDaysMap[day.toLowerCase()])
    : undefined;

  // Map variant IDs to full variant information if roomTypesData is provided
  let selectedVariants;
  if (apiRule.roomVariants?.length > 0) {
    if (roomTypesData && roomTypesData.length > 0) {
      // Map variant IDs to room type and variant data
      selectedVariants = apiRule.roomVariants
        .map((variantId: number) => {
          // Find which room type contains this variant
          for (const roomType of roomTypesData) {
            const variant = roomType.HotelRoomTypeVarientRelData?.find(
              (v: any) => v.variant_id === variantId
            );
            if (variant) {
              return {
                roomTypeId: String(roomType.id),
                variantId: String(variantId),
                variantCode: variant.variant_wise_code || `V${variantId}`,
                variantName: variant.VarientRoomTypeDataRel?.name || 'Unknown Variant'
              };
            }
          }
          // If variant not found in room types, return with minimal info
          return {
            roomTypeId: '',
            variantId: String(variantId),
            variantCode: `V${variantId}`,
            variantName: 'Unknown Variant'
          };
        })
        .filter(v => v !== null);
    } else {
      // Fallback if no room types data available
      selectedVariants = apiRule.roomVariants.map((id: number) => ({
        roomTypeId: '',
        variantId: String(id),
        variantCode: `V${id}`,
        variantName: 'Variant'
      }));
    }
  }

  return {
    id: apiRule.id || '',
    name: apiRule.name || 'Unnamed Rule',
    priority: apiRule.priority || 0,
    isActive: apiRule.isActive ?? true,
    conditions: transformedConditions,
    adjustmentType: apiRule.action?.adjustmentType || 'percentage',
    adjustmentValue: apiRule.action?.adjustmentValue || 0,
    applyTo: apiRule.action?.applyTo === 'base_price' ? 'basePrice' : 'currentPrice',
    roomTypes: (apiRule.roomTypes || []).map((id: number) => String(id)),
    selectedVariants: selectedVariants,
    otaChannels: apiRule.otaChannels || [],
    applicateCmNames: apiRule.applicable_cm_names || [],
    startDate: apiRule.applicableTo?.startDate || new Date().toISOString().split('T')[0],
    endDate: apiRule.applicableTo?.endDate || undefined,
    daysOfWeek: daysOfWeek,
    timeOfDay: apiRule.applicableTo?.timeOfDay || undefined,
    processingFrequency: apiRule.executionType?.processingFrequency || 'manual',
    executionDays: apiRule.executionType?.executionDays || [],
    executionTime: apiRule.executionType?.executionTime || undefined,
    executionStartDate: apiRule.executionType?.executionStartDate || undefined,
    executionEndDate: apiRule.executionType?.executionEndDate || undefined,
    lastRun: apiRule.last_executed_at || undefined,
    createdAt: apiRule.created_at || new Date().toISOString(),
    updatedAt: apiRule.updated_at || new Date().toISOString(),
  };
}
